import {
  SupportBillingChannel,
  SupportCancellationFailure,
  SupportCancellationOutcome,
  SupportCancellationReason,
  SupportPanelStatus,
  SupportSubscriptionStatus,
  SupportSubscriptionSummary
} from 'enums/support-panel';

/** Zoho Desk, and the tickets app that shows the panel beside each ticket. */
const DEFAULT_PARENT_ORIGINS =
  'https://desk.zoho.com.au https://tickets.focusbear.io';

/** The only windows allowed to tell the panel which ticket is open. Space-separated in the env. */
export const SUPPORT_PANEL_PARENT_ORIGINS: string[] = (
  process.env.REACT_APP_SUPPORT_PANEL_PARENT_ORIGINS || DEFAULT_PARENT_ORIGINS
)
  .split(/\s+/)
  .filter(Boolean);

/** Zoho Desk ticket ids are numeric strings; mirrors the API's validation. */
export const ZOHO_TICKET_ID_PATTERN = /^\d{1,30}$/;

export const SUPPORT_PANEL_MESSAGE_KEYS: Record<SupportPanelStatus, string> = {
  [SupportPanelStatus.WAITING_FOR_TICKET]: 'support_panel.waiting_for_ticket',
  [SupportPanelStatus.LOADING]: 'support_panel.loading',
  [SupportPanelStatus.FOUND]: 'support_panel.customer_found',
  [SupportPanelStatus.NOT_FOUND]: 'support_panel.not_found',
  [SupportPanelStatus.MULTIPLE_MATCHES]: 'support_panel.multiple_matches',
  [SupportPanelStatus.NO_CONTACT_EMAIL]: 'support_panel.no_contact_email',
  [SupportPanelStatus.TICKET_NOT_FOUND]: 'support_panel.ticket_not_found',
  [SupportPanelStatus.NOT_AUTHORISED]: 'support_panel.not_authorised',
  [SupportPanelStatus.UNAVAILABLE]: 'support_panel.unavailable',
  [SupportPanelStatus.ZOHO_UNAVAILABLE]: 'support_panel.zoho_unavailable',
  [SupportPanelStatus.AUTH0_UNAVAILABLE]: 'support_panel.auth0_unavailable'
};

/** Outages pass, so these states offer a retry. The others say what to do instead. */
export const SUPPORT_PANEL_RETRYABLE_STATUSES: SupportPanelStatus[] = [
  SupportPanelStatus.UNAVAILABLE,
  SupportPanelStatus.ZOHO_UNAVAILABLE,
  SupportPanelStatus.AUTH0_UNAVAILABLE
];

/** Apple's two stores. Family sharing exists only there, and only the customer can cancel on them. */
export const SUPPORT_APPLE_BILLING_CHANNELS: string[] = [
  SupportBillingChannel.APP_STORE,
  SupportBillingChannel.MAC_APP_STORE
];

/** Summaries that need a sentence; one active subscription speaks for itself. */
export const SUPPORT_SUBSCRIPTION_SUMMARY_KEYS: Partial<
  Record<SupportSubscriptionSummary, string>
> = {
  [SupportSubscriptionSummary.NO_RECORD]:
    'support_panel.subscription_no_record',
  [SupportSubscriptionSummary.NONE_ACTIVE]:
    'support_panel.subscription_none_active',
  [SupportSubscriptionSummary.SEVERAL_ACTIVE]:
    'support_panel.subscription_several_active'
};

/** Stores the API can cancel on. Apple lets only the customer cancel. */
export const SUPPORT_CANCELLABLE_BILLING_CHANNELS: string[] = [
  SupportBillingChannel.STRIPE,
  SupportBillingChannel.PLAY_STORE
];

/** A subscription in one of these states would still take another payment. */
export const SUPPORT_RENEWING_SUBSCRIPTION_STATUSES: SupportSubscriptionStatus[] =
  [SupportSubscriptionStatus.ACTIVE, SupportSubscriptionStatus.BILLING_ISSUE];

export const SUPPORT_CANCELLATION_REASON_KEYS: Record<
  SupportCancellationReason,
  string
> = {
  [SupportCancellationReason.CUSTOMER_REQUEST]:
    'support_panel.cancel_reason_customer_request',
  [SupportCancellationReason.DUPLICATE_SUBSCRIPTION]:
    'support_panel.cancel_reason_duplicate_subscription',
  [SupportCancellationReason.BILLING_ERROR]:
    'support_panel.cancel_reason_billing_error',
  [SupportCancellationReason.OTHER]: 'support_panel.cancel_reason_other'
};

export const SUPPORT_CANCELLATION_OUTCOME_KEYS: Record<
  SupportCancellationOutcome,
  string
> = {
  [SupportCancellationOutcome.CANCELLED]:
    'support_panel.cancel_outcome_cancelled',
  [SupportCancellationOutcome.ALREADY_CANCELLED]:
    'support_panel.cancel_outcome_already_cancelled',
  [SupportCancellationOutcome.NO_SUBSCRIPTION]:
    'support_panel.cancel_outcome_no_subscription',
  [SupportCancellationOutcome.SEVERAL_SUBSCRIPTIONS]:
    'support_panel.cancel_several_renewing',
  [SupportCancellationOutcome.UNSUPPORTED_CHANNEL]:
    'support_panel.cancel_outcome_unsupported_channel',
  [SupportCancellationOutcome.TEAM_PLAN]:
    'support_panel.cancel_outcome_team_plan',
  [SupportCancellationOutcome.PROVIDER_FAILED]:
    'support_panel.cancel_outcome_provider_failed'
};

export const SUPPORT_CANCELLATION_FAILURE_KEYS: Record<
  SupportCancellationFailure,
  string
> = {
  [SupportCancellationFailure.EXPIRED]: 'support_panel.cancel_failure_expired',
  [SupportCancellationFailure.CHANGED]: 'support_panel.cancel_failure_changed',
  [SupportCancellationFailure.NOT_AUTHORISED]: 'support_panel.not_authorised',
  [SupportCancellationFailure.CUSTOMER_NOT_FOUND]:
    'support_panel.cancel_failure_changed',
  [SupportCancellationFailure.UNAVAILABLE]:
    'support_panel.cancel_failure_unavailable',
  [SupportCancellationFailure.OUTCOME_UNKNOWN]:
    'support_panel.cancel_failure_outcome_unknown'
};
